"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import type { KundaliFormCardProps } from "./KundaliFormCard";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import type { KundaliField } from "@/lib/analytics-kundali";

const DEFAULT_DEFER_MS = 3600;
const FORM_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const KundaliFormCard = dynamic(() => import("./KundaliFormCard"), {
  ssr: false,
  loading: () => <KundaliFormLoadingReserve />,
});

// Bundle-size discipline (2026-07-16 trim pass): the returning-seeker card
// (ContinueConsultationCard stack) renders only when browser-local reading
// memory exists, which is discovered after hydration — so it loads as a
// dynamic chunk instead of shipping in every route that embeds the form card.
// Same height reserve as the form shell, so the swap never shifts layout
// (rule 58; the component itself pins a matching min height).
const HomeReturningJourney = dynamic(() => import("./HomeReturningJourney"), {
  ssr: false,
  loading: () => <KundaliFormLoadingReserve />,
});

type DeferredKundaliFormCardProps = KundaliFormCardProps & {
  deferMs?: number;
  interactionOnly?: boolean;
  fallbackTitle?: string;
  fallbackBody?: string;
  fallbackSubmitLabel?: string;
  /** Replace the new-kundali form with the remembered consultation on home. */
  preferRememberedJourney?: boolean;
};

export default function DeferredKundaliFormCard({
  deferMs = DEFAULT_DEFER_MS,
  interactionOnly = false,
  fallbackTitle,
  fallbackBody,
  fallbackSubmitLabel,
  preferRememberedJourney = false,
  ...formProps
}: DeferredKundaliFormCardProps) {
  const [ready, setReady] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState<KundaliField>();
  const activate = useCallback(() => setReady(true), []);
  const activateField = useCallback((field: KundaliField) => {
    setPendingFocusField(field);
    setReady(true);
  }, []);
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );

  useEffect(() => {
    if (ready) return;

    const timer = interactionOnly ? null : window.setTimeout(activate, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of FORM_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      if (timer !== null) window.clearTimeout(timer);
      for (const eventName of FORM_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [activate, deferMs, interactionOnly, ready]);

  if (preferRememberedJourney && rememberedReading) {
    return <HomeReturningJourney reading={rememberedReading} />;
  }

  if (ready) return <KundaliFormCard {...formProps} initialFocusField={pendingFocusField} />;

  return (
    <KundaliFormShell
      containerClassName={formProps.containerClassName}
      title={fallbackTitle}
      body={fallbackBody}
      submitLabel={fallbackSubmitLabel ?? formProps.submitLabel}
      language={formProps.defaultLanguage ?? "en"}
      onActivate={activate}
      onActivateField={activateField}
    />
  );
}

export function KundaliFormShell({
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  title = "Generate your free janam kundali",
  body = "Share your birth details to unlock chart-grounded dosha, dasha, and upay guidance.",
  submitLabel = "Continue",
  language,
  onActivate,
  onActivateField,
}: {
  containerClassName?: string;
  title?: string;
  body?: string;
  submitLabel?: string;
  language: "en" | "hi";
  onActivate: () => void;
  onActivateField: (field: KundaliField) => void;
}) {
  const copy =
    language === "hi"
      ? {
          eyebrow: "Free Vedic Kundali",
          name: "Naam",
          birthDate: "Janam ki date",
          birthTime: "Janam ka time",
          birthPlace: "Janam ka place",
          phone: "WhatsApp number",
          noSignup: "Saved kundali aur account access ke liye. Marketing consent alag hai.",
        }
      : {
          eyebrow: "Free Vedic chart",
          name: "Name",
          birthDate: "Birth date",
          birthTime: "Birth time",
          birthPlace: "Birth place",
          phone: "WhatsApp number",
          noSignup: "For your saved kundali and account access. Marketing consent is separate.",
        };
  const activateTargetField = (target: EventTarget | null) => {
    const field = target instanceof Element ? target.closest<HTMLElement>("[data-field]") : null;
    if (field?.dataset.field) {
      onActivateField(field.dataset.field as KundaliField);
      return;
    }
    onActivate();
  };
  return (
    <div
      className={containerClassName}
      onPointerDown={(event) => activateTargetField(event.target)}
      onFocus={(event) => activateTargetField(event.target)}
    >
      <form
        aria-busy="true"
        className="card-sacred rounded-2xl p-5 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate();
        }}
      >
        <p
          className="mb-1 text-[12px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: "var(--ish-accent)" }}
        >
          {copy.eyebrow}
        </p>
        <h2 className="text-[22px] font-bold leading-tight" style={{ color: "var(--ish-dark)" }}>
          {title}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
          {body}
        </p>
        <div className="mt-5 grid gap-3">
          <ShellField field="name" label={copy.name} />
          <ShellField field="birthDate" label={copy.birthDate} />
          <ShellField field="birthTime" label={copy.birthTime} />
          <ShellField field="birthPlace" label={copy.birthPlace} />
          <ShellField field="phone" label={copy.phone} />
        </div>
        <button
          type="submit"
          className="btn-sacred mt-5 w-full rounded-lg py-3.5 text-[14px] font-semibold"
        >
          {submitLabel}
        </button>
        <p className="mt-3 text-center text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          {copy.noSignup}
        </p>
      </form>
    </div>
  );
}

function KundaliFormLoadingReserve() {
  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 pb-16" aria-hidden>
      <div className="card-sacred min-h-[420px] rounded-2xl p-5 sm:p-6" />
    </div>
  );
}

function ShellField({ field, label }: { field: KundaliField; label: string }) {
  return (
    <div className="block">
      <span
        className="mb-1 block text-[12px] font-medium"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {label}
      </span>
      <button
        type="button"
        data-field={field}
        aria-label={`${label} — open form`}
        className="block h-11 w-full cursor-text rounded-lg"
        style={{
          background: "var(--ish-bg)",
          border: "1px solid var(--ish-border)",
        }}
      />
    </div>
  );
}
